import * as React from "react";
import { useAppDataProvider } from "../dataprovider";

interface LocalRepairCheckResult {
  isLocalRepair: boolean;
  checking: boolean;
  hasError: boolean;
}

/**
 * Shared hook that calls `localrepaircomponents/check` API.
 * Used by both LocalRepairStatusChip and PbhRequestOrderUpdate.
 *
 * @param ownerCode - Owner code to check
 * @param pn - Part number to check
 * @param skip - When true, skips the fetch and returns default false values immediately
 */
export const useLocalRepairCheck = (ownerCode?: string, pn?: string, skip?: boolean): LocalRepairCheckResult => {
  const dataProvider = useAppDataProvider();
  const [isLocalRepair, setIsLocalRepair] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (skip) {
      setIsLocalRepair(false);
      setChecking(false);
      setHasError(false);
      return;
    }
    if (!ownerCode || !pn) {
      setIsLocalRepair(false);
      setChecking(false);
      setHasError(false);
      return;
    }

    setChecking(true);
    setHasError(false);
    dataProvider
      .get("localrepaircomponents/check", { params: { ownerCode, pn } })
      .then(res => {
        setIsLocalRepair(res?.data?.isLocalRepair === true);
      })
      .catch(() => {
        setHasError(true);
        setIsLocalRepair(false);
      })
      .finally(() => {
        setChecking(false);
      });
  }, [ownerCode, pn, skip]);

  return { isLocalRepair, checking, hasError };
};
